import React from 'react';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { Modal, ModalBody, ModalButton, ModalFooter, ModalHeader, ROLE } from 'baseui/modal';
import type { Value } from 'baseui/select';
import { Select, TYPE } from 'baseui/select';
import { Spinner } from 'baseui/spinner';
import { toaster } from 'baseui/toast';
import { useCreateOrUpdateGroup } from '../../hooks/use-create-or-update-group';
import type { UserGroup, UserMinified } from '../../types/userGroup';
type GroupManagementModalProps = {
  showUpdateModal: boolean;
  users: UserMinified[];
  group: UserGroup | null;
  handleCloseModal: () => void;
};
const GroupManagementModal = (props: GroupManagementModalProps) => {
  const {
    showUpdateModal,
    handleCloseModal,
    users,
    group
  } = props;
  const mappedMembers = group?.members.map(id => users.find(u => u.id === id)).filter(member => !!member) ?? [];
  const [groupName, setGroupName] = React.useState<string>(group?.name ?? '');
  const [members, setMembers] = React.useState<Value>(mappedMembers);
  const {
    isPending,
    createOrUpdateGroup
  } = useCreateOrUpdateGroup(group?.id ?? null, groupName, members.map(member => +member.id!));
  const isFormValid = (groupName.length > 0 || group?.id !== null) && members.length > 0;
  const operation = group !== null ? 'Update' : 'Create';
  const onSubmitUserRequest = React.useCallback(() => {
    if (groupName.length === 0 && group?.id === null || setMembers.length < 0) {
      return;
    }
    createOrUpdateGroup({
      groupId: group?.id ?? null,
      groupName,
      members: members.map(members => +members.id!)
    }, {
      onSuccess: ({
        data
      }) => {
        toaster.positive('Group operation done successfully', {
          key: 'createOrUpdateUserGroupSuccess'
        });
        handleCloseModal();
      },
      onError: error => {
        toaster.negative(<>Failed creating or updating user: {error.message}</>, {
          key: 'createOrUpdateUserGroupError'
        });
      }
    });
  }, [group, groupName, members, createOrUpdateGroup, handleCloseModal]);
  if (isPending) {
    return <Spinner />;
  }
  return <Modal onClose={() => handleCloseModal()} isOpen={showUpdateModal} animate autoFocus role={ROLE.dialog}>
      <ModalHeader>{operation} Group Form</ModalHeader>
      <ModalBody>
        <FormControl label={() => 'Name'} caption={() => 'Name of the group'}>
          <Input value={groupName} onChange={e => setGroupName(e.target.value)} disabled={group !== null} />
        </FormControl>
        <Select options={users} labelKey="email" valueKey="id" placeholder="Choose members" maxDropdownHeight="300px" type={TYPE.search} multi onChange={({
        value
      }: {
        value: Value;
      }) => {
        setMembers(value);
      }} value={members} />
      </ModalBody>
      <ModalFooter>
        <ModalButton onClick={() => handleCloseModal()}>Cancel</ModalButton>
        <ModalButton disabled={!isFormValid} onClick={onSubmitUserRequest}>
          {operation} Group
        </ModalButton>
      </ModalFooter>
    </Modal>;
};
export default GroupManagementModal;